import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { AdaptParentContext } from "@tamagui/adapt";
import { useComposedRefs } from "@tamagui/compose-refs";
import {
  Theme,
  getAnimationDriver,
  isClient,
  isTouchable,
  isWeb,
  mergeEvent,
  styled,
  themeable,
  useDidFinishSSR,
  useEvent,
  useIsomorphicLayoutEffect,
  useThemeName,
  withStaticProperties
} from "@tamagui/core";
import { Portal } from "@tamagui/portal";
import { RemoveScroll } from "@tamagui/remove-scroll";
import { XStack, YStack } from "@tamagui/stacks";
import { useConstant } from "@tamagui/use-constant";
import { useControllableState } from "@tamagui/use-controllable-state";
import { useKeyboardVisible } from "@tamagui/use-keyboard-visible";
import React, {
  createContext,
  forwardRef,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  PanResponder
} from "react-native";
import { SHEET_HANDLE_NAME, SHEET_NAME } from "./constants";
import { SheetProvider, useSheetContext } from "./SheetContext";
import { SheetScrollView } from "./SheetScrollView";
import { createSheetScope } from "./SheetContext";
const SheetHandleFrame = styled(XStack, {
  name: SHEET_HANDLE_NAME,
  height: 10,
  borderRadius: 100,
  backgroundColor: "$background",
  zIndex: 10,
  marginHorizontal: "35%",
  marginBottom: "$2",
  opacity: 0.5,
  hoverStyle: {
    opacity: 0.7
  },
  variants: {
    open: {
      true: {
        pointerEvents: "auto"
      },
      false: {
        opacity: 0,
        pointerEvents: "none"
      }
    }
  }
});
const SheetHandle = SheetHandleFrame.extractable(
  ({ __scopeSheet, ...props }) => {
    const context = useSheetContext(SHEET_HANDLE_NAME, __scopeSheet);
    return /* @__PURE__ */ jsx(
      SheetHandleFrame,
      {
        onPress: () => {
          const max = context.snapPoints.length + (context.dismissOnSnapToBottom ? -1 : 0);
          const nextPos = (context.position + 1) % max;
          context.setPosition(nextPos);
        },
        open: context.open,
        ...props
      }
    );
  }
);
const SHEET_OVERLAY_NAME = "SheetOverlay";
const SheetOverlayFrame = styled(YStack, {
  name: SHEET_OVERLAY_NAME,
  backgroundColor: "$color10",
  fullscreen: true,
  opacity: 0.5,
  zIndex: 0,
  variants: {
    closed: {
      true: {
        opacity: 0,
        pointerEvents: "none"
      },
      false: {
        pointerEvents: "auto"
      }
    }
  }
});
const SheetOverlay = SheetOverlayFrame.extractable(
  ({ __scopeSheet, ...props }) => {
    const context = useSheetContext(SHEET_OVERLAY_NAME, __scopeSheet);
    return /* @__PURE__ */ jsx(
      SheetOverlayFrame,
      {
        closed: !context.open || context.hidden,
        ...props,
        onPress: mergeEvent(
          props.onPress,
          context.dismissOnOverlayPress ? () => {
            context.setOpen(false);
          } : void 0
        )
      }
    );
  }
);
const selectionStyleSheet = isClient ? document.createElement("style") : null;
if (selectionStyleSheet) {
  document.head.appendChild(selectionStyleSheet);
}
const SheetFrameFrame = styled(YStack, {
  name: SHEET_NAME,
  flex: 1,
  backgroundColor: "$background",
  borderTopLeftRadius: "$true",
  borderTopRightRadius: "$true",
  width: "100%",
  maxHeight: "100%",
  overflow: "hidden",
  pointerEvents: "auto"
});
const SheetFrame = SheetFrameFrame.extractable(
  forwardRef(
    ({ __scopeSheet, ...props }, forwardedRef) => {
      const context = useSheetContext(SHEET_NAME, __scopeSheet);
      const composedContentRef = useComposedRefs(forwardedRef, context.contentRef);
      return /* @__PURE__ */ jsx(SheetFrameFrame, { ref: composedContentRef, ...props });
    }
  )
);
const HIDDEN_SIZE = 1e4;
const sheetComponents = {
  Handle: SheetHandle,
  Frame: SheetFrame,
  Overlay: SheetOverlay,
  ScrollView: SheetScrollView
};
const ParentSheetContext = createContext({
  zIndex: 40
});
const useSheetContoller = () => {
  const controller = useContext(SheetControllerContext);
  const isHidden = controller == null ? void 0 : controller.hidden;
  const isShowingNonSheet = isHidden && (controller == null ? void 0 : controller.open);
  return {
    controller,
    isHidden,
    isShowingNonSheet
  };
};
const Sheet = withStaticProperties(
  forwardRef(function Sheet2(props, ref) {
    const hydrated = useDidFinishSSR();
    const { isShowingNonSheet } = useSheetContoller();
    if (isShowingNonSheet || !hydrated) {
      return null;
    }
    return /* @__PURE__ */ jsx(SheetImplementation, { ref, ...props });
  }),
  sheetComponents
);
const SheetImplementation = themeable(
  forwardRef(function SheetImplementation2(props, ref) {
    const parentSheet = useContext(ParentSheetContext);
    const { isHidden, controller } = useSheetContoller();
    const {
      __scopeSheet,
      snapPoints: snapPointsProp = [80],
      open: openProp,
      defaultOpen,
      children: childrenProp,
      position: positionProp,
      onPositionChange,
      onOpenChange,
      defaultPosition,
      dismissOnOverlayPress = true,
      animationConfig,
      dismissOnSnapToBottom = false,
      forceRemoveScrollEnabled = null,
      disableDrag: disableDragProp,
      modal = false,
      zIndex = parentSheet.zIndex + 1,
      portalProps
    } = props;
    if (process.env.NODE_ENV === "development") {
      if (snapPointsProp.some((p) => p < 0 || p > 100)) {
        console.warn(
          "\u26A0\uFE0F Invalid snapPoint given, snapPoints must be between 0 and 100, equal to percent height of frame"
        );
      }
    }
    const driver = getAnimationDriver();
    if (!driver) {
      throw new Error("Must set animations in tamagui.config.ts");
    }
    const disableDrag = disableDragProp ?? (controller == null ? void 0 : controller.disableDrag);
    const keyboardIsVisible = useKeyboardVisible();
    const themeName = useThemeName();
    const contentRef = React.useRef(null);
    const scrollBridge = useConstant(() => ({
      enabled: false,
      y: 0,
      paneY: 0,
      paneMinY: 0,
      scrollStartY: -1,
      drag: () => {
      },
      release: () => {
      },
      scrollLock: false
    }));
    const onOpenChangeInternal = (val) => {
      var _a;
      (_a = controller == null ? void 0 : controller.onOpenChange) == null ? void 0 : _a.call(controller, val);
      onOpenChange == null ? void 0 : onOpenChange(val);
    };
    const [open, setOpen] = useControllableState({
      prop: (controller == null ? void 0 : controller.open) ?? openProp,
      defaultProp: true,
      onChange: onOpenChangeInternal,
      strategy: "most-recent-wins",
      transition: true
    });
    const [frameSize, setFrameSize] = useState(0);
    const snapPoints = useMemo(
      () => dismissOnSnapToBottom ? [...snapPointsProp, 0] : snapPointsProp,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [JSON.stringify(snapPointsProp), dismissOnSnapToBottom]
    );
    const [position_, setPosition_] = useControllableState({
      prop: positionProp,
      defaultProp: defaultPosition || (open ? 0 : -1),
      onChange: onPositionChange,
      strategy: "most-recent-wins",
      transition: true
    });
    const position = open === false ? -1 : position_;
    if (open && dismissOnSnapToBottom && position === snapPoints.length - 1) {
      setPosition_(0);
    }
    const setPosition = useCallback(
      (next) => {
        if (dismissOnSnapToBottom && next === snapPoints.length - 1) {
          setOpen(false);
        } else {
          setPosition_(next);
        }
      },
      [dismissOnSnapToBottom, snapPoints.length, setPosition_, setOpen]
    );
    const { useAnimatedNumber, useAnimatedNumberReaction, useAnimatedNumberStyle } = driver;
    const animatedNumber = useAnimatedNumber(HIDDEN_SIZE);
    const at = useRef(0);
    useAnimatedNumberReaction(animatedNumber, (value) => {
      at.current = value;
      scrollBridge.paneY = value;
    });
    const [isResizing, setIsResizing] = useState(true);
    useIsomorphicLayoutEffect(() => {
      if (!isResizing) {
        setIsResizing(true);
      }
    }, [modal]);
    function stopSpring() {
      animatedNumber.stop();
      if (scrollBridge.onFinishAnimate) {
        scrollBridge.onFinishAnimate();
        scrollBridge.onFinishAnimate = void 0;
      }
    }
    const shouldSetPositionOpen = open && position < 0;
    useEffect(() => {
      if (shouldSetPositionOpen) {
        setPosition(0);
      }
    }, [setPosition, shouldSetPositionOpen]);
    const positions = useMemo(
      () => snapPoints.map((point) => getPercentSize(point, frameSize)),
      [frameSize, snapPoints]
    );
    const [opacity, setOpacity] = useState(open ? 1 : 0);
    if (open && opacity === 0) {
      setOpacity(1);
    }
    useEffect(() => {
      if (!open) {
        const tm = setTimeout(() => {
          setOpacity(0);
        }, 400);
        return () => {
          clearTimeout(tm);
        };
      }
    }, [open]);
    const animateTo = useEvent((position2) => {
      const current = animatedNumber.getValue();
      if (isHidden && open)
        return;
      if (!current)
        return;
      if (frameSize === 0)
        return;
      const hiddenValue = frameSize === 0 ? HIDDEN_SIZE : frameSize;
      const toValue = isHidden || position2 === -1 ? hiddenValue : positions[position2];
      if (at.current === toValue)
        return;
      stopSpring();
      if (isHidden || isResizing) {
        if (isResizing) {
          setIsResizing(false);
        }
        animatedNumber.setValue(toValue, {
          type: "timing",
          duration: 0
        });
        at.current = toValue;
        return;
      }
      const overshootClamping = at.current === HIDDEN_SIZE;
      animatedNumber.setValue(toValue, {
        ...animationConfig,
        type: "spring",
        overshootClamping
      });
    });
    useIsomorphicLayoutEffect(() => {
      animateTo(position);
    }, [isHidden, frameSize, position, animateTo]);
    const [isShowingInnerSheet, setIsShowingInnerSheet] = useState(false);
    const shouldHideParentSheet = !isWeb && modal && isShowingInnerSheet;
    const parentSheetContext = useContext(SheetInsideSheetContext);
    const onInnerSheet = useCallback((hasChild) => {
      setIsShowingInnerSheet(hasChild);
    }, []);
    const panResponder = useMemo(
      () => {
        if (disableDrag)
          return;
        if (!frameSize)
          return;
        if (isShowingInnerSheet)
          return;
        const minY = positions[0];
        scrollBridge.paneMinY = minY;
        let startY = at.current;
        function makeUnselectable(val) {
          if (!selectionStyleSheet)
            return;
          if (!val) {
            selectionStyleSheet.innerText = "";
          } else {
            selectionStyleSheet.innerText = ":root * { user-select: none !important; -webkit-user-select: none !important; }";
          }
        }
        const release = ({ vy, dragAt }) => {
          isExternalDrag = false;
          previouslyScrolling = false;
          makeUnselectable(false);
          const at2 = dragAt + startY;
          const end = at2 + frameSize * vy * 0.2;
          let closestPoint = 0;
          let dist = Infinity;
          for (let i = 0; i < positions.length; i++) {
            const position2 = positions[i];
            const curDist = end > position2 ? end - position2 : position2 - end;
            if (curDist < dist) {
              dist = curDist;
              closestPoint = i;
            }
          }
          setPosition(closestPoint);
          animateTo(closestPoint);
        };
        const finish = (_e, state) => {
          release({
            vy: state.vy,
            dragAt: state.dy
          });
        };
        let previouslyScrolling = false;
        const onMoveShouldSet = (_e, { dy }) => {
          const isScrolled = scrollBridge.y !== 0;
          const isDraggingUp = dy < 0;
          const isNearTop = scrollBridge.paneY - 5 <= scrollBridge.paneMinY;
          if (isScrolled) {
            previouslyScrolling = true;
            return false;
          }
          if (isNearTop) {
            if (!isScrolled && isDraggingUp) {
              return false;
            }
          }
          return Math.abs(dy) > 5;
        };
        const grant = () => {
          makeUnselectable(true);
          stopSpring();
          startY = at.current;
        };
        let isExternalDrag = false;
        scrollBridge.drag = (dy) => {
          if (!isExternalDrag) {
            isExternalDrag = true;
            grant();
          }
          const to = dy + startY;
          animatedNumber.setValue(resisted(to, minY), { type: "direct" });
        };
        scrollBridge.release = release;
        return PanResponder.create({
          onMoveShouldSetPanResponder: onMoveShouldSet,
          onPanResponderGrant: grant,
          onPanResponderMove: (_e, { dy }) => {
            const toFull = dy + startY;
            const to = resisted(toFull, minY);
            animatedNumber.setValue(to, { type: "direct" });
          },
          onPanResponderEnd: finish,
          onPanResponderTerminate: finish,
          onPanResponderRelease: finish
        });
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [disableDrag, isShowingInnerSheet, animateTo, frameSize, positions, setPosition]
    );
    let handleComponent = null;
    let overlayComponent = null;
    let frameComponent = null;
    React.Children.forEach(childrenProp, (child) => {
      var _a, _b;
      if (isValidElement(child)) {
        const name = (_b = (_a = child.type) == null ? void 0 : _a["staticConfig"]) == null ? void 0 : _b.componentName;
        switch (name) {
          case "SheetHandle":
            handleComponent = child;
            break;
          case "Sheet":
            frameComponent = child;
            break;
          case "SheetOverlay":
            overlayComponent = child;
            break;
          default:
            console.warn("Warning: passed invalid child to Sheet", child);
        }
      }
    });
    const animatedStyle = useAnimatedNumberStyle(animatedNumber, (val) => {
      const translateY = frameSize === 0 ? HIDDEN_SIZE : val;
      return {
        transform: [{ translateY }]
      };
    });
    const AnimatedView = driver["NumberView"] ?? driver.View;
    useIsomorphicLayoutEffect(() => {
      if (!(parentSheetContext && open))
        return;
      parentSheetContext(true);
      return () => {
        parentSheetContext(false);
      };
    }, [parentSheetContext, open]);
    const nextParentContext = useMemo(
      () => ({
        zIndex
      }),
      [zIndex]
    );
    const handleLayout = useCallback((e) => {
      var _a;
      let next = (_a = e.nativeEvent) == null ? void 0 : _a.layout.height;
      if (isWeb && isTouchable && !open) {
        next += 100;
      }
      if (!next)
        return;
      setFrameSize((prev) => {
        const isBigChange = Math.abs(prev - next) > 20;
        if (!keyboardIsVisible) {
          setIsResizing(isBigChange);
        }
        return next;
      });
    }, [keyboardIsVisible]);
    const removeScrollEnabled = forceRemoveScrollEnabled ?? (open && modal);
    const contents = /* @__PURE__ */ jsx(ParentSheetContext.Provider, { value: nextParentContext, children: /* @__PURE__ */ jsxs(
      SheetProvider,
      {
        modal,
        contentRef,
        frameSize,
        dismissOnOverlayPress,
        dismissOnSnapToBottom,
        open,
        hidden: !!isHidden,
        scope: __scopeSheet,
        position,
        snapPoints,
        setPosition,
        setOpen,
        scrollBridge,
        children: [
          isResizing || shouldHideParentSheet ? null : overlayComponent,
          /* @__PURE__ */ jsxs(
            AnimatedView,
            {
              ref,
              ...panResponder == null ? void 0 : panResponder.panHandlers,
              onLayout: handleLayout,
              pointerEvents: open && !shouldHideParentSheet ? "auto" : "none",
              style: [
                {
                  position: "absolute",
                  zIndex,
                  width: "100%",
                  height: "100%",
                  opacity
                },
                animatedStyle
              ],
              children: [
                handleComponent,
                /* @__PURE__ */ jsx(
                  RemoveScroll,
                  {
                    forwardProps: true,
                    enabled: removeScrollEnabled,
                    allowPinchZoom: true,
                    shards: [contentRef],
                    removeScrollBar: false,
                    children: isResizing ? /* @__PURE__ */ jsx(Fragment, {}) : frameComponent
                  }
                )
              ]
            }
          )
        ]
      }
    ) });
    const adaptContext = useContext(AdaptParentContext);
    if (modal) {
      const modalContents = /* @__PURE__ */ jsx(Portal, { zIndex, ...portalProps, children: /* @__PURE__ */ jsx(Theme, { forceClassName: true, name: themeName, children: /* @__PURE__ */ jsx(AdaptParentContext.Provider, { value: adaptContext, children: contents }) }) });
      if (isWeb) {
        return modalContents;
      }
      return /* @__PURE__ */ jsx(SheetInsideSheetContext.Provider, { value: onInnerSheet, children: modalContents });
    }
    return contents;
  }),
  { componentName: "Sheet" }
);
const SheetInsideSheetContext = createContext(null);
const ControlledSheet = Sheet;
function getPercentSize(point, frameSize) {
  if (!frameSize)
    return 0;
  if (point === void 0) {
    console.warn("No snapPoint");
    return 0;
  }
  const pct = point / 100;
  const next = Math.round(frameSize - pct * frameSize);
  return next;
}
function resisted(y, minY, maxOverflow = 25) {
  if (y < minY) {
    const past = minY - y;
    const pctPast = Math.min(maxOverflow, past) / maxOverflow;
    const diminishBy = 1.1 - Math.pow(0.1, pctPast);
    const extra = -diminishBy * maxOverflow;
    return minY + extra;
  }
  return y;
}
const SheetControllerContext = createContext(null);
const SheetController = ({
  children,
  onOpenChange: onOpenChangeProp,
  ...value
}) => {
  const onOpenChange = useEvent(onOpenChangeProp);
  const memoValue = useMemo(
    () => ({
      open: value.open,
      hidden: value.hidden,
      disableDrag: value.disableDrag,
      onOpenChange
    }),
    [onOpenChange, value.open, value.hidden, value.disableDrag]
  );
  return /* @__PURE__ */ jsx(SheetControllerContext.Provider, { value: memoValue, children });
};
export {
  ControlledSheet,
  Sheet,
  SheetController,
  SheetFrame,
  SheetFrameFrame,
  SheetHandle,
  SheetHandleFrame,
  SheetOverlay,
  SheetOverlayFrame,
  createSheetScope
};
//# sourceMappingURL=Sheet.js.map
